/* ==========================================================================
   Combo Pack Price Cards — logic (v2: multi-variant aware)

   Works on ANY product page automatically.
   - Product has NO "Combo" option            -> script does nothing.
   - Product has ONLY the "Combo" option      -> cards show each pack's price.
   - Product has Combo + other options        -> cards show the price of
     [currently selected other options + that pack], recalculated live
     whenever the customer changes any other option (Size, Quantity, ...).

   HOW PRICES ARE CALCULATED
   For every Combo card we build a full selection:
       selection[otherOption] = what is currently selected on the page
       selection[comboOption] = that card's own value
   and look up the exact variant in the product JSON. If a combination does
   not exist, we fall back to the closest matching variant.

   HOW "CURRENTLY SELECTED" IS READ (first hit wins, per option):
     1. checked radio / select whose name matches the option name
     2. checked radio / select whose value belongs to that option
     3. ?variant=ID in the URL, or the form's hidden id input
     4. first variant of the product

   OTHER NOTES
   - Glossy re-renders variant fieldsets on option change. A MutationObserver
     rebuilds our cards instantly from cached product data (no network wait).
   - MutationObserver callbacks are async, so our own DOM writes are
     discarded with observer.takeRecords() (a plain boolean flag does NOT
     work for that) — this prevents infinite rebuild loops.
   - Set DEBUG = true to log the selection used for each price in console.
   ========================================================================== */

(function () {
  'use strict';

  var OPTION_NAME = 'combo'; // matched case-insensitively against the option name
  var DEBUG = false;

  var productCache = {};    // handle -> product JSON
  var productPromises = {}; // handle -> in-flight fetch (avoid duplicate requests)
  var observer = null;
  var isEnhancing = false;
  var rafPending = false;
  var settleTimers = [];

  // ---- Boot -----------------------------------------------------------

  ready(function () {
    boot();
    observeDom();
    patchHistory();
  });

  document.addEventListener('shopify:section:load', boot); // theme editor preview
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) boot(); // bfcache restore
  });

  function ready(fn) {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function boot() {
    var fieldset = findComboFieldset();
    if (!fieldset) return; // no Combo option on this product

    var handle = getProductHandle();
    if (!handle) return;

    if (productCache[handle]) {
      enhance(fieldset, productCache[handle]);
      return;
    }

    if (!productPromises[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productPromises[handle] = fetch(root + 'products/' + handle + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (product) {
          if (product) productCache[handle] = product;
          return product;
        })
        .catch(function () { return null; });
    }

    productPromises[handle].then(function (product) {
      if (!product) return; // fail silently — plain radios still work
      var fs = findComboFieldset(); // re-query: DOM may have been replaced meanwhile
      if (fs) enhance(fs, product);
    });
  }

  // ---- Observer: theme wiped / replaced markup -----------------------------

  function observeDom() {
    var target = document.querySelector('main') || document.body;

    observer = new MutationObserver(function (mutations) {
      if (isEnhancing) return;

      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (!m.addedNodes || !m.addedNodes.length) continue;
        if (m.target && m.target.closest && m.target.closest('.combo-price-option')) continue;
        relevant = true;
        break;
      }
      if (!relevant) return;

      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        refreshNow();
      });
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  // Run our own DOM writes without triggering the observer.
  function guarded(fn) {
    isEnhancing = true;
    try {
      fn();
    } finally {
      if (observer) observer.takeRecords(); // discard mutations we just caused
      isEnhancing = false;
    }
  }

  // ---- Finders -----------------------------------------------------------

  function findComboFieldset() {
    // Theme names radios like "Combo-1-{section-id}-{product-id}".
    var radios = document.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      var name = (radios[i].getAttribute('name') || '').toLowerCase();
      if (name === OPTION_NAME || name.indexOf(OPTION_NAME + '-') === 0) {
        var fs = radios[i].closest('fieldset');
        if (fs) return fs;
      }
    }
    return null;
  }

  function getProductHandle() {
    var match = window.location.pathname.match(/\/products\/([a-zA-Z0-9\-_%]+)/);
    return match ? match[1] : null;
  }

  // The area of the page that belongs to THIS product form
  // (so related products / quick-view modals are not mixed in).
  function getScope(fieldset) {
    return (
      fieldset.closest('product-info') ||
      fieldset.closest('[data-product-id]') ||
      fieldset.closest('.product') ||
      fieldset.closest('.shopify-section') ||
      fieldset.closest('form') ||
      document
    );
  }

  function getRadios(fieldset) {
    return Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
  }

  // ---- Product option helpers --------------------------------------------

  function getOptionName(product, j) {
    var o = product.options[j];
    var name = typeof o === 'string' ? o : (o && o.name) || '';
    return String(name).toLowerCase();
  }

  function getOptionValues(product, j) {
    var o = product.options[j];
    if (o && typeof o === 'object' && o.values) return o.values;
    // Older JSON shape: derive values from variants
    var key = 'option' + (j + 1);
    var seen = [];
    product.variants.forEach(function (v) {
      if (v[key] != null && seen.indexOf(v[key]) === -1) seen.push(v[key]);
    });
    return seen;
  }

  function getComboIndex(product, sampleValue) {
    // 1) by option NAME (reliable)
    for (var j = 0; j < product.options.length; j++) {
      if (getOptionName(product, j) === OPTION_NAME) return j;
    }
    // 2) fallback: by value
    for (var k = 0; k < product.options.length; k++) {
      if (getOptionValues(product, k).indexOf(sampleValue) !== -1) return k;
    }
    return 0;
  }

  function parsePackCount(value) {
    var match = String(value).match(/(\d+)/);
    var count = match ? parseInt(match[1], 10) : 1;
    return count > 0 ? count : 1;
  }

  // ---- Enhance / build ---------------------------------------------------

  function enhance(fieldset, product) {
    var radios = getRadios(fieldset);
    if (!radios.length) return;

    guarded(function () {
      if (fieldset.dataset.comboEnhanced !== 'true') {
        fieldset.dataset.comboEnhanced = 'true';
        fieldset.classList.add('combo-price-fieldset');

        var maxPackCount = 0;
        radios.forEach(function (radio) {
          var count = parsePackCount(radio.value);
          if (count > maxPackCount) maxPackCount = count;
        });

        radios.forEach(function (radio) {
          buildCard(radio, maxPackCount);
        });
      }

      refreshPrices(fieldset, product);
    });

    // Safety: make sure one card is selected (only if theme selected none).
    var current = getRadios(fieldset);
    var anyChecked = current.some(function (r) { return r.checked; });
    if (!anyChecked && current.length) {
      current[0].checked = true;
      current[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function buildCard(radio, maxPackCount) {
    var scope = radio.closest('fieldset') || radio.closest('form') || document;
    var label = scope.querySelector('label[for="' + cssEscape(radio.id) + '"]');

    var wrapper = document.createElement('label');
    wrapper.setAttribute('for', radio.id);
    wrapper.className = 'combo-price-option';

    var packCount = parsePackCount(radio.value);
    var badgeHtml = '';

    if (packCount === maxPackCount && maxPackCount > 1) {
      badgeHtml = '<span class="combo-price-badge">Value Pack</span>';
      wrapper.className += ' has-badge';
    }

    wrapper.innerHTML =
      '<span class="combo-price-card">' +
        badgeHtml +
        '<span class="combo-price-left">' +
          '<span class="combo-price-radio"></span>' +
          '<span class="combo-price-text">' +
            '<span class="combo-price-name">' + escapeHtml(radio.value) + '</span>' +
            '<span class="combo-price-save" data-combo-save-badge></span>' +
          '</span>' +
        '</span>' +
        '<span class="combo-price-right">' +
          '<span class="combo-price-total" data-combo-total-price></span>' +
          '<span class="combo-price-compare" data-combo-compare-price></span>' +
        '</span>' +
      '</span>';

    // Always wrap the radio, even if its original label wasn't found.
    if (label && label.parentNode) {
      label.parentNode.insertBefore(wrapper, label);
      wrapper.insertBefore(radio, wrapper.firstChild);
      label.remove();
    } else {
      radio.parentNode.insertBefore(wrapper, radio);
      wrapper.insertBefore(radio, wrapper.firstChild);
    }
  }

  function cssEscape(id) {
    if (window.CSS && CSS.escape) return CSS.escape(id);
    return String(id).replace(/([ #;&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');
  }

  // ---- Reading what the customer has selected (other options) -------------

  // "options[Quantity]" -> "quantity"
  function cleanName(name) {
    return String(name || '').toLowerCase().replace(/^options\[/, '').replace(/\]$/, '');
  }

  function nameMatchesOption(inputName, optionName) {
    var n = cleanName(inputName);
    if (!n || !optionName) return false;
    return n === optionName || n.indexOf(optionName + '-') === 0 || n.indexOf(optionName + '_') === 0;
  }

  function getCheckedInputs(scope, fieldset) {
    var out = [];
    var radios = scope.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      if (fieldset.contains(radios[i])) continue; // skip the combo fieldset itself
      out.push({ name: radios[i].getAttribute('name'), value: radios[i].value });
    }
    var selects = scope.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      if (fieldset.contains(selects[s])) continue;
      var sel = selects[s];
      if (sel.name === 'id') continue; // variant-id select, not an option picker
      out.push({ name: sel.getAttribute('name'), value: sel.value });
    }
    return out;
  }

  function getCurrentVariantFromPage(scope, product) {
    var id = null;
    var urlMatch = window.location.search.match(/[?&]variant=(\d+)/);
    if (urlMatch) id = urlMatch[1];

    if (!id) {
      var idInput = scope.querySelector('form input[name="id"], form select[name="id"], input[name="id"]');
      if (idInput && idInput.value) id = idInput.value;
    }
    if (!id) return null;

    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  // Returns an array (one slot per product option) with the selected value,
  // or null for the combo slot / anything we truly couldn't determine.
  function readSelection(fieldset, product, comboIndex) {
    var scope = getScope(fieldset);
    var checked = getCheckedInputs(scope, fieldset);
    var pageVariant = null; // lazy
    var selection = [];

    for (var j = 0; j < product.options.length; j++) {
      if (j === comboIndex) { selection[j] = null; continue; }

      var optName = getOptionName(product, j);
      var optValues = getOptionValues(product, j);
      var found = null;

      // 1) match by input name
      for (var a = 0; a < checked.length && found === null; a++) {
        if (nameMatchesOption(checked[a].name, optName) && optValues.indexOf(checked[a].value) !== -1) {
          found = checked[a].value;
        }
      }

      // 2) match by value (only if that value is not ambiguous across options)
      for (var b = 0; b < checked.length && found === null; b++) {
        var v = checked[b].value;
        if (optValues.indexOf(v) === -1) continue;
        var ambiguous = false;
        for (var k = 0; k < product.options.length; k++) {
          if (k !== j && getOptionValues(product, k).indexOf(v) !== -1) { ambiguous = true; break; }
        }
        if (!ambiguous) found = v;
      }

      // 3) variant from URL / form id
      if (found === null) {
        if (pageVariant === null) pageVariant = getCurrentVariantFromPage(scope, product) || false;
        if (pageVariant) found = pageVariant['option' + (j + 1)];
      }

      // 4) first variant
      if (found === null && product.variants.length) {
        found = product.variants[0]['option' + (j + 1)];
      }

      selection[j] = found;
    }

    return selection;
  }

  // ---- Pricing -----------------------------------------------------------

  function findVariant(product, selection, comboIndex, comboValue) {
    var comboKey = 'option' + (comboIndex + 1);
    var best = null;
    var bestMatched = -1;

    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      if (variant[comboKey] !== comboValue) continue;

      var matched = 0;
      var mismatch = false;

      for (var j = 0; j < product.options.length; j++) {
        if (j === comboIndex || selection[j] == null) continue;
        if (variant['option' + (j + 1)] === selection[j]) {
          matched++;
        } else {
          mismatch = true;
        }
      }

      if (!mismatch) return variant; // exact match

      if (matched > bestMatched) { // closest fallback
        best = variant;
        bestMatched = matched;
      }
    }

    return best;
  }

  function refreshPrices(fieldset, product) {
    var radios = getRadios(fieldset);
    if (!radios.length) return;

    var comboIndex = getComboIndex(product, radios[0].value);
    var selection = readSelection(fieldset, product, comboIndex);

    if (DEBUG) {
      console.log('[combo-price-picker] other selections:', selection.slice(), 'comboIndex:', comboIndex);
    }

    radios.forEach(function (radio) {
      var variant = findVariant(product, selection, comboIndex, radio.value);
      if (!variant) return;

      var wrapper = radio.closest('.combo-price-option');
      if (!wrapper) return;

      var saveEl = wrapper.querySelector('[data-combo-save-badge]');
      var totalEl = wrapper.querySelector('[data-combo-total-price]');
      var compareEl = wrapper.querySelector('[data-combo-compare-price]');

      if (totalEl) totalEl.textContent = formatMoney(variant.price);

      var hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (hasDiscount) {
        if (compareEl) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        }
        var savePercent = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        if (saveEl) {
          if (savePercent > 0) {
            saveEl.textContent = 'Save ' + savePercent + '%';
            saveEl.style.display = '';
          } else {
            saveEl.textContent = '';
            saveEl.style.display = 'none';
          }
        }
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (saveEl) {
          saveEl.textContent = '';
          saveEl.style.display = 'none';
        }
      }
    });
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
    } catch (e) {}

    var amount = cents / 100;
    var rounded = Math.round(amount);
    var symbol = '\u20B9'; // ₹
    return symbol + (rounded === amount ? rounded : amount.toFixed(2));
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---- Refresh triggers ----------------------------------------------------

  // Re-enhance (if theme wiped us) or just recalculate prices.
  function refreshNow() {
    var fieldset = findComboFieldset();
    if (!fieldset) return;

    var handle = getProductHandle();
    if (!handle || !productCache[handle]) {
      boot();
      return;
    }

    if (fieldset.dataset.comboEnhanced !== 'true') {
      enhance(fieldset, productCache[handle]);
    } else {
      guarded(function () {
        refreshPrices(fieldset, productCache[handle]);
      });
    }
  }

  // Theme may update its own state (URL, hidden inputs, re-render) a moment
  // AFTER the change event, so recalculate now and again shortly after.
  function scheduleRefresh() {
    refreshNow();

    settleTimers.forEach(clearTimeout);
    settleTimers = [];

    requestAnimationFrame(refreshNow);
    settleTimers.push(setTimeout(refreshNow, 80));
    settleTimers.push(setTimeout(refreshNow, 350));
  }

  // Any option change (radio / dropdown). Capture phase so it still runs
  // even if the theme stops event propagation.
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!t) return;
    if (t.type === 'radio' || t.tagName === 'SELECT') scheduleRefresh();
  }, true);

  // Themes usually update ?variant= via history.replaceState after a change.
  function patchHistory() {
    if (window.__comboHistoryPatched) return;
    window.__comboHistoryPatched = true;

    ['pushState', 'replaceState'].forEach(function (method) {
      var original = history[method];
      if (typeof original !== 'function') return;
      history[method] = function () {
        var result = original.apply(this, arguments);
        scheduleRefresh();
        return result;
      };
    });

    window.addEventListener('popstate', scheduleRefresh);
  }

  // Common theme variant-change events (harmless if never fired).
  ['variant:change', 'variantChange', 'product:variant-change', 'variant-change'].forEach(function (name) {
    document.addEventListener(name, scheduleRefresh);
  });
})();